<template>
  <div class="flex h-full flex-col">
    <div class="flex flex-1 flex-col gap-12">
      <div>{{ formattedHeaderText }}</div>

      <RevLink
        v-if="props.type === FORM_TYPES.UPDATES_CONTACTS"
        :to="{ name: ROUTE_NAMES.PROFILE.LEGAL_DETAILS }"
      >
        {{ i18n(translations.setDefaultContact) }}
      </RevLink>

      <RevInfoBlock
        v-if="!isExporting"
        class="my-2"
        :icon="IconLightbulb"
        :title="i18n(translations.prepareCsvFile)"
        variant="info"
      >
        <template v-if="props.type === FORM_TYPES.UPDATE">
          <UpdateInfoBlockContent />
        </template>

        <template v-if="props.type === FORM_TYPES.UPDATES_CONTACTS">
          <UpdateContactsInfoBlockContent />
        </template>

        <template
          v-if="
            props.type === FORM_TYPES.IMPORT ||
            props.type === FORM_TYPES.IMPORT_NEW
          "
        >
          <ImportInfoBlockContent
            :is-premium-grade-listings-enabled
            :should-show-additional-info="props.type === FORM_TYPES.IMPORT"
            :type="props.type"
          />
        </template>
      </RevInfoBlock>

      <RevInputSelect
        v-if="isFeatureEnabled('multiEncodingEnabled')"
        id="select-encoding"
        v-model="encoding"
        :aria-label="i18n(translations.selectEncoding)"
        :label="i18n(translations.encodingLabel)"
        :options="encodingOptions"
      />

      <RevInputSelect
        v-if="!isExporting"
        id="select-delimiter"
        v-model="delimiter"
        :aria-label="i18n(translations.selectDelimiter)"
        :label="i18n(translations.delimiterLabel)"
        :options="delimiterOptions"
      />

      <RevInputFileUpload
        v-if="!isExporting"
        id="input-files"
        v-model="uploadedFiles"
        :accept="['.csv']"
        :description="i18n(translations.formatDescription)"
        :label-empty="i18n(translations.selectFile)"
        :max-files="1"
      />

      <RevInputSelect
        v-if="!isUpdatingContact"
        id="select-market"
        v-model="market"
        :aria-label="i18n(translations.selectMarket)"
        :description="
          !isExporting ? i18n(translations.marketSelectReminder) : ''
        "
        :label="i18n(translations.market)"
        :options="marketOptions"
      />

      <RevInputSelect
        v-if="isExporting"
        id="select-publication-state"
        v-model="publicationStateInput"
        :aria-label="i18n(translations.selectPublicationStates)"
        :label="i18n(translations.publicationState)"
        :options="publicationStates"
      />
    </div>

    <RevButton
      :disabled="!canSubmit"
      :loading="isSubmitting"
      variant="primary"
      @click="handleSubmit"
    >
      {{ i18n(actionsConfig[props.type].label) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { catalogAccessAPI, sellerProfileAPI } from '@backmarket/http-api'
import { postListingsExport } from '@backmarket/http-api/src/api-specs-catalog/catalog-access/listings'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevLink } from '@ds/components/Link'
import { IconLightbulb } from '@ds/icons/IconLightbulb'

import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { FORM_TYPES, type FormTypes } from '../ImportExportDrawer.constants'

import translations from './Form.translations'
import ImportInfoBlockContent from './components/ImportInfoBlockContent.vue'
import UpdateContactsInfoBlockContent from './components/UpdateContactsInfoBlockContent.vue'
import UpdateInfoBlockContent from './components/UpdateInfoBlockContent.vue'

const { isFeatureEnabled } = useBusinessFeaturesStore()
const tracking = useTracking()
const { allMarkets } = useMarketplace()

const emit = defineEmits<{
  (e: 'result', payload: string): void
  (e: 'submitted'): void
}>()

const props = defineProps<{
  type: FormTypes[keyof FormTypes]
  publicationState: string
}>()

const i18n = useI18n()
const { isPremiumGradeListingsEnabled } = useSellerInfoStore()

const publicationStates = [
  {
    value: 'all',
    label: i18n(translations.allPublicationStates),
  },
  {
    value: 'ACTIVE',
    label: i18n(translations.publicationStateOnline),
  },
  {
    value: 'MISSING_PRICE_OR_COMMENT',
    label: i18n(translations.publicationStateMissingInformation),
  },
  {
    value: 'IN_VALIDATION',
    label: i18n(translations.publicationStateInValidation),
  },
  { value: 'DISABLED', label: i18n(translations.publicationStateOffline) },
]

const encodingOptions = [
  { value: 'utf-8', label: 'UTF-8' },
  { value: 'latin1', label: 'Latin-1 (CP-1252)' },
]

const delimiterOptions = [
  {
    value: ';',
    label: i18n(translations.semicolon),
  },
  {
    value: ',',
    label: i18n(translations.comma),
  },
  {
    value: '\\t',
    label: i18n(translations.tab),
  },
]

const marketOptions = [
  {
    label: i18n(translations.allMarkets),
    value: 'all',
  },
  ...Object.keys(allMarkets).map((countryCode) => ({
    label: i18n.country(countryCode),
    value: countryCode,
  })),
]

const formattedHeaderText = computed(() => {
  const translationsKeys = {
    [FORM_TYPES.EXPORT]: translations.exportListingHeaderText,
    [FORM_TYPES.IMPORT]: translations.importListingHeaderText,
    [FORM_TYPES.IMPORT_NEW]: translations.importListingHeaderTextNew,
    [FORM_TYPES.UPDATE]: translations.updateListingHeaderText,
    [FORM_TYPES.UPDATES_CONTACTS]: translations.updateContactsHeaderText,
  }

  return i18n(translationsKeys[props.type])
})

const isExporting = computed(() => {
  return props.type === FORM_TYPES.EXPORT
})

const encoding = ref(encodingOptions[0].value)
const delimiter = ref(delimiterOptions[0].value)
const uploadedFiles = ref([])
const market = ref(marketOptions[0].value)
const publicationStateInput = ref('')
const isSubmitting = ref(false)

const canSubmit = computed(() => {
  let condition = Boolean(delimiter.value && market.value)

  if (isFeatureEnabled('multiEncodingEnabled')) {
    condition = condition && Boolean(encoding.value)
  }

  if (!isExporting.value) {
    condition = condition && Boolean(uploadedFiles.value.length)
  }

  return condition
})

const isUpdatingContact = computed(() => props.type === 'updates-contacts')

const actionsConfig = {
  [FORM_TYPES.EXPORT]: {
    label: translations.export,
    request: postListingsExport,
  },
  [FORM_TYPES.IMPORT]: {
    label: translations.import,
    request: catalogAccessAPI.postListingsBatchUpsertWithProducts,
  },
  [FORM_TYPES.IMPORT_NEW]: {
    label: translations.import,
    request: catalogAccessAPI.postListingsBatchUpsertWithProducts,
  },
  [FORM_TYPES.UPDATE]: {
    label: translations.update,
    request: catalogAccessAPI.postListingsBatchUpdate,
  },
  [FORM_TYPES.UPDATES_CONTACTS]: {
    label: translations.updateContacts,
    request: sellerProfileAPI.bulkUpdateDsaContactDetails,
  },
}

async function handleSubmit() {
  let data: Record<string, string> | FormData = {}

  if (isExporting.value) {
    data = { encoding: encoding.value, market: market.value }

    if (publicationStateInput.value !== 'all') {
      data.publicationState = publicationStateInput.value
    }
  } else {
    // TODO [FRONT-586] Use header on endpoint definition instead
    data = new FormData()
    data.append('file', uploadedFiles.value[0])
    data.append('delimiter', delimiter.value)
    if (isFeatureEnabled('multiEncodingEnabled')) {
      data.append('encoding', encoding.value)
    }
    data.append('market', market.value)
  }

  try {
    isSubmitting.value = true
    await $httpFetch(actionsConfig[props.type].request, {
      body: data,
    })

    tracking.trackFormSubmit({
      page_type: 'listings',
      zone: 'listings_action_panel',
      name: `csv_${props.type}`,
    })

    emit('result', `${props.type}/success`)
  } catch (err) {
    emit('result', `${props.type}/error`)
  } finally {
    isSubmitting.value = false
    emit('submitted')
  }
}
</script>
